import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { dialogPaperSx, primaryBtnSx, ghostBtnSx, PT } from '../portalUi';

interface CaptchaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (token: string) => void;
  title?: string;
  description?: string;
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

type TurnstileHandlers = {
  success: (token: string) => void;
  error: () => void;
  expired: () => void;
  timeout: () => void;
  unsupported: () => void;
};

export const CaptchaModal: React.FC<CaptchaModalProps> = ({
  open,
  onClose,
  onSuccess,
  title = 'Verify you are human',
  description = 'Please complete the verification to continue'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  /* The latest handlers, assigned on every render below. The widget's callbacks
   * go through this, so a widget drawn renders after the open still reaches the
   * `onSuccess` the parent holds NOW — never a stale closure over old form data. */
  const handlers = useRef<TurnstileHandlers | null>(null);

  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  /* Draws the widget once both the script and the holder are there, called by
   * whichever arrives second: the script's load, or the holder's callback ref
   * below (and by the effect when both already are). `widgetId` keeps it to one
   * widget per open. */
  const renderTurnstile = React.useCallback(() => {
    if (widgetId.current) return;
    if (turnstileRef.current && window.turnstile) {
      widgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => handlers.current?.success(token),
        'error-callback': () => handlers.current?.error(),
        'expired-callback': () => handlers.current?.expired(),
        'timeout-callback': () => handlers.current?.timeout(),
        'unsupported-callback': () => handlers.current?.unsupported(),
        theme: 'dark',
        size: 'normal',
        retry: 'auto',
        'retry-interval': 8000,
        'refresh-expired': 'auto',
        'refresh-timeout': 'auto'
      });
    }
  }, [SITE_KEY]);

  /* The holder is a CALLBACK ref, and that is what makes every open draw.
   *
   * MUI's Dialog mounts its content through a Portal that renders it in a
   * SECOND pass (the Portal sets its mount node in a layout effect), and React
   * runs the effect below before that pass. So once the script had loaded, the
   * effect found no holder and drew nothing: the empty box on every open after
   * the first. The first open only worked because the script's `load` came
   * later, after the content had mounted. A callback ref runs the moment the
   * holder exists. */
  const holderRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      turnstileRef.current = node;
      if (node) renderTurnstile();
    },
    [renderTurnstile]
  );

  React.useEffect(() => {
    let script: HTMLScriptElement | null = null;
    const onLoad = () => renderTurnstile();
    const onError = () => {
      /* Dropped, so the next open loads it afresh instead of waiting on a tag
         that will never fire again. */
      script?.remove();
      setError(
        'Failed to load captcha. Please check your internet connection and try again.'
      );
    };

    if (open) {
      // Load Turnstile script if not already loaded
      // Note: For CSP compliance, ensure your Content Security Policy includes:
      // script-src https://challenges.cloudflare.com; frame-src https://challenges.cloudflare.com
      if (!window.turnstile) {
        /* One script per page. A tag that is already loading — this component's
           from an earlier open, or CaptchaWidget's (it marks its own
           `data-turnstile`, as this one now does) — is reused and waited on. */
        script = document.querySelector<HTMLScriptElement>(
          `script[src^="${TURNSTILE_SRC}"]`
        );
        if (!script) {
          script = document.createElement('script');
          script.src = TURNSTILE_SRC;
          script.async = true;
          script.defer = true;
          script.dataset.turnstile = 'true';
          document.head.appendChild(script);
        }
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
      } else {
        renderTurnstile();
      }
    }

    return () => {
      script?.removeEventListener('load', onLoad);
      script?.removeEventListener('error', onError);
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* Already gone with its container. */
        }
        widgetId.current = null;
      }
    };
  }, [open, renderTurnstile]);

  const handleCaptchaSuccess = (token: string) => {
    setIsLoading(false);
    setError(null);
    onSuccess(token);
  };

  const handleCaptchaError = () => {
    setIsLoading(false);
    setError('Captcha verification failed. Please try again.');
  };

  const handleCaptchaExpired = () => {
    setIsLoading(false);
    setError('Captcha expired. Please try again.');
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  };

  const handleCaptchaTimeout = () => {
    setIsLoading(false);
    setError('Captcha timed out. Please try again.');
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  };

  const handleCaptchaUnsupported = () => {
    setIsLoading(false);
    setError(
      'Your browser does not support the captcha widget. Please try using a different browser.'
    );
  };

  handlers.current = {
    success: handleCaptchaSuccess,
    error: handleCaptchaError,
    expired: handleCaptchaExpired,
    timeout: handleCaptchaTimeout,
    unsupported: handleCaptchaUnsupported
  };

  const handleClose = () => {
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetId.current);
      } catch {
        /* Already gone with its container. */
      }
      widgetId.current = null;
    }
    setError(null);
    setIsLoading(false);
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 4, px: 3 }}>
        <Box
          sx={{
            mx: 'auto',
            mb: 2,
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(56,232,255,0.14)',
            border: `1px solid ${PT.tealBorder}`
          }}
        >
          <VerifiedUserIcon sx={{ fontSize: 28, color: PT.teal }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 20, color: PT.text }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1, color: PT.textMuted, fontSize: 14 }}>
          {description}
        </Typography>

        {error && (
          <Typography
            sx={{ mt: 2, color: '#ff7a7a', fontSize: 13, fontWeight: 700 }}
          >
            {error}
          </Typography>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress sx={{ color: PT.teal }} />
          </Box>
        )}

        <Box
          ref={holderRef}
          sx={{
            mt: 2.5,
            display: 'flex',
            justifyContent: 'center',
            minHeight: '65px',
            width: '100%'
          }}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
        {error && (
          <Button onClick={handleRetry} sx={{ ...primaryBtnSx, minWidth: 110 }}>
            Retry
          </Button>
        )}
        <Button onClick={handleClose} sx={{ ...ghostBtnSx, minWidth: 110 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
